import { useCallback, useEffect, useRef, useState } from 'react';
import {
	useWarehouseTransactionDocApi,
	useWarehouseTransactionDocItemApi,
} from 'hooks';

const CURRENT_INVENTORY_SOURCES = new Set(['ORIGINAL', 'ADJUSTMENT']);
const ISO_8601_PATTERN =
	/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:\d{2})$/;

const isRecord = value =>
	value !== null && typeof value === 'object' && !Array.isArray(value);

const isPositiveInteger = value => Number.isSafeInteger(value) && value > 0;

const isIso8601 = value =>
	typeof value === 'string' &&
	ISO_8601_PATTERN.test(value) &&
	Number.isFinite(Date.parse(value));

export class InvalidCurrentInventoryContractError extends Error {
	constructor() {
		super('La respuesta del inventario actual no cumple el contrato esperado.');
		this.name = 'InvalidCurrentInventoryContractError';
	}
}

const invalidCurrentInventory = () => {
	throw new InvalidCurrentInventoryContractError();
};

export const resolveBillingMaterialsInventory = response => {
	if (!isRecord(response)) invalidCurrentInventory();
	if (!Object.prototype.hasOwnProperty.call(response, 'currentInventory'))
		return response.inventory || [];

	const { currentInventory } = response;
	if (currentInventory === null) return [];
	if (
		!isRecord(currentInventory) ||
		!CURRENT_INVENTORY_SOURCES.has(currentInventory.source) ||
		!isPositiveInteger(currentInventory.documentId) ||
		!Array.isArray(currentInventory.items) ||
		currentInventory.items.some(item => !isRecord(item))
	)
		invalidCurrentInventory();

	if (currentInventory.source === 'ORIGINAL') {
		if (
			currentInventory.originalDocumentId !== null ||
			currentInventory.appliedAt !== null
		)
			invalidCurrentInventory();
	} else if (
		!isPositiveInteger(currentInventory.originalDocumentId) ||
		!isIso8601(currentInventory.appliedAt)
	) {
		invalidCurrentInventory();
	}

	return currentInventory.items;
};

export default function useBillingMaterials(orderId) {
	const mountedRef = useRef(false);
	const orderIdRef = useRef(orderId);
	const generationRef = useRef(0);
	orderIdRef.current = orderId;
	const [state, setState] = useState({
		orderId,
		isFetching: true,
		transactionDoc: {},
		inventory: [],
		error: null,
	});
	const { getBillingMaterials } = useWarehouseTransactionDocApi();
	const { updateTransactionItems } = useWarehouseTransactionDocItemApi();

	const refresh = useCallback(
		(reset = false) => {
			const requestedOrderId = orderId;
			if (!mountedRef.current || !requestedOrderId) return Promise.resolve();
			const generation = generationRef.current + 1;
			generationRef.current = generation;
			setState(current => ({
				...(reset || current.orderId !== requestedOrderId
					? {
							orderId: requestedOrderId,
							transactionDoc: {},
							inventory: [],
						}
					: current),
				isFetching: true,
				error: null,
			}));
			return getBillingMaterials(requestedOrderId)
				.then(response => {
					if (
						!mountedRef.current ||
						orderIdRef.current !== requestedOrderId ||
						generationRef.current !== generation
					)
						return;
					const inventory = resolveBillingMaterialsInventory(response);
					const transactionDoc = { ...response };
					delete transactionDoc.inventory;
					delete transactionDoc.currentInventory;
					delete transactionDoc.comments;
					setState({
						orderId: requestedOrderId,
						transactionDoc,
						inventory: inventory || [],
						isFetching: false,
						error: null,
					});
				})
				.catch(error => {
					if (
						mountedRef.current &&
						orderIdRef.current === requestedOrderId &&
						generationRef.current === generation
					)
						setState(current => ({
							...current,
							...(error instanceof InvalidCurrentInventoryContractError
								? { transactionDoc: {}, inventory: [] }
								: {}),
							isFetching: false,
							error,
						}));
				});
		},
		[orderId, getBillingMaterials],
	);

	useEffect(() => {
		mountedRef.current = true;
		refresh(true);
		return () => {
			mountedRef.current = false;
			generationRef.current += 1;
		};
	}, [orderId]);

	const updateItem = item =>
		updateTransactionItems([item], 'disapprove').then(() => refresh());

	return { ...state, refresh, updateItem };
}
